import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { AppIconButton } from '../../components/design-system';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { tenantDevicesService } from '../../services/TenantDevicesService';
import { efemService } from '../../services/EfemService';
import type { OpsDeviceDetail, TenantDeviceRecord } from '@cnapse/shared/schemas.js';
import { EfemInstallationCard } from '../../components/efem/commissioning/EfemInstallationCard';

import { getDeviceCategoryLabel, PRODUCT_NAMES } from '@cnapse/shared';
const STATUS_LABELS: Record<string, string> = {
  'in-stock': 'In Stock',
  allocated: 'Allocated',
  shipped: 'Shipped',
  claimed: 'Claimed',
  active: 'Active',
  returned: 'Returned',
  retired: 'Retired',
};

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box display="flex" alignItems="baseline" gap={1} py={0.75}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontFamily="monospace" component="div">
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

function navigateTo(path: string) {
  const prefix = window.location.pathname.split('/devices')[0] || '';
  window.history.pushState({}, '', `${prefix}${path}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export default function TenantDeviceDetail() {
  const serialFromPath = (): string => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    return decodeURIComponent(parts[parts.length - 1] ?? '');
  };

  const [serialNumber] = useState(serialFromPath);
  const [device, setDevice] = useState<TenantDeviceRecord | null>(null);
  const [gateways, setGateways] = useState<
    Array<{ serialNumber: string; label: string; gatewaySerialNumber: string }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!serialNumber) return;
    setLoading(true);
    setError(null);
    try {
      const res = await tenantDevicesService.getTenantDevice(serialNumber);
      setDevice(res.data.device);
      const { clients } = await efemService.getEfemClients();
      setGateways(
        clients
          .filter((c) => c.gatewaySerialNumber)
          .map((c) => ({
            serialNumber: c.gatewaySerialNumber!,
            label: c.name ?? c.gatewaySerialNumber!,
            gatewaySerialNumber: c.gatewaySerialNumber!,
          })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load device');
    } finally {
      setLoading(false);
    }
  }, [serialNumber]);

  useEffect(() => {
    void load();
  }, [load]);

  const isPeripheral =
    device?.deviceCategory === 'barcode-scanner' ||
    device?.deviceCategory === 'handheld-barcode-scanner' ||
    device?.deviceCategory === 'waferid-camera';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Tooltip title="Back to devices">
          <AppIconButton onClick={() => navigateTo('/devices/claims')} size="small">
            <BackIcon />
          </AppIconButton>
        </Tooltip>
        <Box flex={1}>
          <Typography variant="h5" fontWeight="bold">
            Device detail
          </Typography>
          <Typography variant="body2" fontFamily="monospace" color="text.secondary">
            {serialNumber}
          </Typography>
        </Box>
        {device?.inventoryStatus && (
          <Chip
            size="small"
            label={STATUS_LABELS[device.inventoryStatus] ?? device.inventoryStatus}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {device && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Device summary
          </Typography>
          <DetailRow label="Category" value={getDeviceCategoryLabel(device.deviceCategory)} />
          <DetailRow label="Model" value={device.deviceModel} />
          <DetailRow label="Order" value={device.orderReference ?? '—'} />
          {device.vendorSerialNumber && (
            <DetailRow label="Vendor serial" value={device.vendorSerialNumber} />
          )}
          {device.macAddress && <DetailRow label="MAC address" value={device.macAddress} />}
        </Paper>
      )}

      {device && isPeripheral && (
        <>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {PRODUCT_NAMES.AXON} installation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bind this peripheral to a {PRODUCT_NAMES.AXON}, load port (scanner), or wafer imaging slot (camera).
          </Typography>
          <EfemInstallationCard
            device={device as unknown as OpsDeviceDetail}
            portal="tenant"
            gateways={gateways}
            onUpdated={() => void load()}
          />
        </>
      )}

      {device && !isPeripheral && (
        <Alert severity="info">
          {PRODUCT_NAMES.AXON} peripheral binding applies to barcode scanners and wafer ID cameras only.
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />
      <Typography variant="body2" color="text.secondary">
        Configure all gateways and load ports from{' '}
        <Typography
          component="span"
          variant="body2"
          color="primary"
          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => navigateTo('/devices/claims')}
        >
          Devices
        </Typography>
        {' '}(commissioning panel).
      </Typography>
    </Container>
  );
}
